// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/polpo/engine.ts
// 🔧 PURPOSE: Le regole di "Polpo Goloso" (il gioco del labirinto), senza
//   disegni né React.
//   - Il polpo di Mola gira per un labirinto e mangia i taralli (10 punti);
//     quattro gabbiani lo inseguono, ognuno a modo suo (uno punta dritto al
//     polpo, uno gli taglia la strada davanti, uno fa la tenaglia con il
//     primo, uno si avvicina e poi si stanca). Ogni tanto tornano ai loro
//     angoli, poi riprendono la caccia.
//   - Il peperoncino (50) fa scappare i gabbiani per qualche secondo: presi
//     valgono 200, 400, 800, 1.600 e tornano al nido a riprendersi.
//   - Due volte a livello compare il panzerotto (500). Una vita in più a
//     10.000 punti. Tre vite.
//   - Finiti i taralli si passa al labirinto dopo: il borgo antico di Mola,
//     i trulli di Alberobello, Polignano a Mare; poi si ricomincia, più veloce.
//   Il tavolo è una griglia (19×21): le celle hanno il centro sui numeri
//   interi; si gira solo al centro delle celle. Si esce da un lato e si
//   rientra dall'altro (il tunnel).
//   Tutto cambia dentro `step()`, a piccoli passi (1/120 di secondo).
// ============================================

export const UP = 0;
export const LEFT = 1;
export const DOWN = 2;
export const RIGHT = 3;
export const NONE = -1;
export const DX = [0, -1, 0, 1];
export const DY = [-1, 0, 1, 0];
export const reverseOf = (d: number) => (d < 0 ? d : (d + 2) % 4);

export const MAZE_KEYS = ['mola', 'alberobello', 'polignano'] as const;
export type MazeKey = (typeof MAZE_KEYS)[number];

/** # muro · . tarallo · o peperoncino · - porta del nido · G nido · P partenza del polpo. */
const LAYOUTS: Record<MazeKey, string[]> = {
  mola: [
    "###################",
    "#o.......#.......o#",
    "#.##.###.#.###.##.#",
    "#.................#",
    "#.##.#.#####.#.##.#",
    "#....#...#...#....#",
    "####.###.#.###.####",
    "####.#.......#.####",
    "####.#.##-##.#.####",
    "    ...#GGG#...    ",
    "####.#.#####.#.####",
    "####.#.......#.####",
    "####.#.#####.#.####",
    "#........#........#",
    "#.##.###.#.###.##.#",
    "#o.#.....P.....#.o#",
    "##.#.#.#####.#.#.##",
    "#....#...#...#....#",
    "#.######.#.######.#",
    "#.................#",
    "###################",
  ],
  alberobello: [
    "###################",
    "#o...............o#",
    "#.##.##.###.##.##.#",
    "#.##.##.###.##.##.#",
    "#.................#",
    "#....##.#.#.##....#",
    "####.##.#.#.##.####",
    "####.#.......#.####",
    "####.#.##-##.#.####",
    "    ...#GGG#...    ",
    "####.#.#####.#.####",
    "####.#.......#.####",
    "####.#.#####.#.####",
    "#.................#",
    "#.##.##.###.##.##.#",
    "#o##.##.###.##.##o#",
    "#........P........#",
    "#.##.#.#####.#.##.#",
    "#.##.#...#...#.##.#",
    "#.................#",
    "###################",
  ],
  polignano: [
    "###################",
    "#o..##.......##..o#",
    "#.#.##.#####.##.#.#",
    "#.#.##.......##.#.#",
    "#.......#.#.......#",
    "##.####.#.#.####.##",
    "##...##.....##...##",
    "####.#.......#.####",
    "####.#.##-##.#.####",
    "    ...#GGG#...    ",
    "####.#.#####.#.####",
    "####.#.......#.####",
    "####.#.#####.#.####",
    "#.................#",
    "#.###.#.###.#.###.#",
    "#o..#....P....#..o#",
    "###.#.#.###.#.#.###",
    "#.....#.....#.....#",
    "#.###.###.###.###.#",
    "#.................#",
    "###################",
  ],
};

export const OPEN = 0;
export const WALL = 1;
export const DOOR = 2;
export const HOUSE = 3;
export const TARALLO = 1;
export const PEPPER = 2;

export interface Cell {
  c: number;
  r: number;
}

export interface Maze {
  key: MazeKey;
  cols: number;
  rows: number;
  /** OPEN, WALL, DOOR, HOUSE per cella (riga per riga). */
  cells: Uint8Array;
  /** I taralli all'inizio del livello (TARALLO, PEPPER o 0). */
  dots: Uint8Array;
  dotCount: number;
  start: Cell;
  door: Cell;
  /** La cella sopra la porta: da lì escono i gabbiani. */
  exit: Cell;
  /** La riga dentro il nido. */
  houseRow: number;
  /** Passi fino all'uscita del nido da ogni cella (la strada dei gabbiani presi). */
  homeDist: Int16Array;
}

const mazes = new Map<MazeKey, Maze>();
export function getMaze(key: MazeKey): Maze {
  let m = mazes.get(key);
  if (m) return m;
  const rows = LAYOUTS[key];
  const cols = rows[0].length;
  const cells = new Uint8Array(cols * rows.length);
  const dots = new Uint8Array(cols * rows.length);
  let start: Cell = { c: 0, r: 0 };
  let door: Cell = { c: 0, r: 0 };
  let houseRow = 0;
  let dotCount = 0;
  rows.forEach((line, r) => Array.from(line).forEach((ch, c) => {
    const i = r * cols + c;
    if (ch === '#') cells[i] = WALL;
    else if (ch === '-') {
      cells[i] = DOOR;
      door = { c, r };
    } else if (ch === 'G') {
      cells[i] = HOUSE;
      houseRow = r;
    } else if (ch === '.' || ch === 'o') {
      dots[i] = ch === 'o' ? PEPPER : TARALLO;
      dotCount += 1;
    } else if (ch === 'P') start = { c, r };
  }));
  const exit = { c: door.c, r: door.r - 1 };
  // La strada più corta verso il nido, da ogni cella (con il tunnel).
  const homeDist = new Int16Array(cols * rows.length).fill(-1);
  homeDist[exit.r * cols + exit.c] = 0;
  const queue = [exit];
  for (let q = 0; q < queue.length; q += 1) {
    const { c, r } = queue[q];
    for (let d = 0; d < 4; d += 1) {
      const nc = (c + DX[d] + cols) % cols;
      const nr = r + DY[d];
      const ni = nr * cols + nc;
      if (nr < 0 || nr >= rows.length || cells[ni] !== OPEN || homeDist[ni] >= 0) continue;
      homeDist[ni] = homeDist[r * cols + c] + 1;
      queue.push({ c: nc, r: nr });
    }
  }
  m = { key, cols, rows: rows.length, cells, dots, dotCount, start, door, exit, houseRow, homeDist };
  mazes.set(key, m);
  return m;
}

export const mazeKeyForLevel = (level: number): MazeKey => MAZE_KEYS[(Math.max(1, level) - 1) % MAZE_KEYS.length];

const wrapCol = (m: Maze, c: number) => ((c % m.cols) + m.cols) % m.cols;
export const cellAt = (m: Maze, c: number, r: number): number => (r < 0 || r >= m.rows ? WALL : m.cells[r * m.cols + wrapCol(m, c)]);
/** Ci si può camminare (il nido e la porta no: lì i gabbiani entrano ed escono a parte). */
export const walkable = (m: Maze, c: number, r: number) => cellAt(m, c, r) === OPEN;

// ---- La partita ------------------------------------------------------------------------

export interface Actor {
  x: number;
  y: number;
  dir: number;
}

export interface Polpo extends Actor {
  /** La direzione chiesta (dal dito o dalle frecce): si prende appena si può. */
  want: number;
}

export type GullMode = 'house' | 'leaving' | 'active' | 'eaten' | 'entering';

export interface Gull extends Actor {
  id: number;
  mode: GullMode;
  /** Spaventato dal peperoncino. */
  scared: boolean;
  /** Deve fare inversione al prossimo passo. */
  turnBack: boolean;
}

export type Phase = 'ready' | 'play' | 'dying' | 'cleared' | 'over';

export interface Game {
  level: number;
  mazeKey: MazeKey;
  dots: Uint8Array;
  dotsLeft: number;
  /** Taralli (e peperoncini) mangiati in questo livello: il panzerotto esce a 70 e a 140. */
  levelDots: number;
  score: number;
  lives: number;
  /** Taralli (e peperoncini) mangiati in tutta la partita (per i controlli del server). */
  dotsEaten: number;
  extraLife: boolean;
  polpo: Polpo;
  gulls: Gull[];
  phase: Phase;
  phaseT: number;
  /** Tempo che scorre sempre (per le animazioni). */
  t: number;
  /** Tempo di gioco in questa vita (uscite dal nido). */
  playT: number;
  /** Orologio di caccia/ritirata (si ferma con il peperoncino). */
  modeT: number;
  chase: boolean;
  frightT: number;
  chain: number;
  bonusT: number;
  bonusCount: number;
}

export type GameEvent =
  | { type: 'dot' }
  | { type: 'pepper' }
  | { type: 'gull'; points: number; x: number; y: number }
  | { type: 'bonus'; points: number; x: number; y: number }
  | { type: 'life' }
  | { type: 'death' }
  | { type: 'cleared' }
  | { type: 'over' };

export type Random = () => number;

export const START_LIVES = 3;
export const EXTRA_LIFE_AT = 10000;
export const POINTS = { tarallo: 10, pepper: 50, bonus: 500, gulls: [200, 400, 800, 1600] };
export const READY_S = 2;
export const DEATH_S = 1.4;
export const CLEARED_S = 2;
const SUB = 1 / 120;
const BONUS_AT = [70, 140];
const BONUS_S = 9.5;

export const polpoSpeed = (level: number) => Math.min(8, 6.2 + 0.2 * (level - 1));
export const gullSpeed = (level: number) => Math.min(8.3, 5.7 + 0.25 * (level - 1));
export const SCARED_SPEED = 3.6;
export const EATEN_SPEED = 12;
const HOUSE_SPEED = 3.2;
/** Quanto dura il peperoncino: 6 secondi al primo livello, poi meno. */
export const frightSeconds = (level: number) => Math.max(1, 6 - 0.6 * (level - 1));
/** Quando escono dal nido (secondi di gioco); più presto nei livelli alti. */
const releaseAt = (id: number, level: number) => [0, 1, 4, 7][id] * Math.max(0.4, 1 - 0.1 * (level - 1));
/** Ritirata e caccia, a turno: [ritirata, caccia, ritirata, caccia, …], poi solo caccia. */
const schedule = (level: number) => {
  const s = level === 1 ? 7 : Math.max(2, 7 - 1.5 * (level - 1));
  const s2 = level === 1 ? 5 : Math.max(1, 5 - (level - 1));
  return [s, 20, s, 20, s2, 20, s2];
};

function resetActors(g: Game): void {
  const m = getMaze(g.mazeKey);
  g.polpo = { x: m.start.c, y: m.start.r, dir: LEFT, want: LEFT };
  const inHouse = [m.door.c, m.door.c - 1, m.door.c + 1];
  g.gulls = [0, 1, 2, 3].map((id) => (id === 0
    ? { id, x: m.exit.c, y: m.exit.r, dir: LEFT, mode: 'active' as GullMode, scared: false, turnBack: false }
    : { id, x: inHouse[id - 1], y: m.houseRow, dir: UP, mode: 'house' as GullMode, scared: false, turnBack: false }));
  g.playT = 0;
  g.modeT = 0;
  g.chase = false;
  g.frightT = 0;
  g.chain = 0;
  g.bonusT = 0;
}

function startLevel(g: Game, level: number): void {
  const m = getMaze(mazeKeyForLevel(level));
  g.level = level;
  g.mazeKey = m.key;
  g.dots = m.dots.slice();
  g.dotsLeft = m.dotCount;
  g.levelDots = 0;
  g.bonusCount = 0;
  resetActors(g);
  g.phase = 'ready';
  g.phaseT = 0;
}

export function newGame(): Game {
  const g = {
    score: 0,
    lives: START_LIVES,
    dotsEaten: 0,
    extraLife: false,
    t: 0,
  } as Game;
  startLevel(g, 1);
  return g;
}

// ---- Movimento -------------------------------------------------------------------------

const near = (v: number) => Math.abs(v - Math.round(v)) < 1e-6;
export const atCenter = (a: Actor) => near(a.x) && near(a.y);

/**
 * Sposta `a` di `dist` lungo la sua direzione; a ogni centro di cella
 * `decide` sceglie la direzione nuova (NONE: si ferma lì). Il tunnel: dalla
 * colonna -1 si rientra dall'ultima, e viceversa.
 */
function advance(m: Maze, a: Actor, dist: number, decide: (c: number, r: number) => number): void {
  let guard = 0;
  while (dist > 1e-9 && a.dir !== NONE && guard < 64) {
    guard += 1;
    const horiz = a.dir === LEFT || a.dir === RIGHT;
    const s = a.dir === RIGHT || a.dir === DOWN ? 1 : -1;
    const pos = horiz ? a.x : a.y;
    const next = s > 0 ? Math.floor(pos + 1e-6) + 1 : Math.ceil(pos - 1e-6) - 1;
    const to = Math.abs(next - pos);
    // (con un margine: 8,9 + 0,1 fa 9,000000000000002 e il centro non va saltato)
    if (dist < to - 1e-6) {
      if (horiz) a.x += s * dist;
      else a.y += s * dist;
      return;
    }
    dist = Math.max(0, dist - to);
    if (horiz) a.x = next;
    else a.y = next;
    if (a.x < 0) a.x += m.cols;
    else if (a.x > m.cols - 1) a.x -= m.cols;
    a.dir = decide(Math.round(a.x), Math.round(a.y));
  }
}

const isTunnel = (m: Maze, x: number, y: number) => {
  const r = Math.round(y);
  return cellAt(m, 0, r) === OPEN && (x < 3 || x > m.cols - 4);
};

/** Dove punta un gabbiano. */
export function gullTarget(g: Game, gull: Gull): [number, number] {
  const m = getMaze(g.mazeKey);
  if (gull.mode === 'eaten') return [m.exit.c, m.exit.r];
  const corners: Array<[number, number]> = [[m.cols - 2, -3], [1, -3], [m.cols - 1, m.rows + 1], [0, m.rows + 1]];
  if (!g.chase) return corners[gull.id];
  const p = g.polpo;
  const pc = Math.round(p.x);
  const pr = Math.round(p.y);
  const pd = p.dir === NONE ? (p.want === NONE ? LEFT : p.want) : p.dir;
  switch (gull.id) {
    case 0:
      return [pc, pr];
    case 1:
      return [pc + 4 * DX[pd], pr + 4 * DY[pd]];
    case 2: {
      const ax = pc + 2 * DX[pd];
      const ay = pr + 2 * DY[pd];
      const g0 = g.gulls[0];
      return [2 * ax - g0.x, 2 * ay - g0.y];
    }
    default:
      return (gull.x - pc) ** 2 + (gull.y - pr) ** 2 > 64 ? [pc, pr] : corners[3];
  }
}

function moveGull(g: Game, gull: Gull, h: number, rnd: Random): void {
  const m = getMaze(g.mazeKey);
  if (gull.mode === 'house') return;
  if (gull.mode === 'leaving' || gull.mode === 'entering') {
    // Fuori dal nido: prima in centro, poi su fino sopra la porta. Dentro: giù fino in fondo.
    const v = (gull.mode === 'entering' ? EATEN_SPEED / 2 : HOUSE_SPEED) * h;
    if (gull.mode === 'entering') {
      gull.y = Math.min(m.houseRow, gull.y + v);
      if (gull.y >= m.houseRow) gull.mode = 'leaving';
      return;
    }
    if (Math.abs(gull.x - m.door.c) > 1e-6) {
      gull.x += Math.sign(m.door.c - gull.x) * Math.min(v, Math.abs(m.door.c - gull.x));
      return;
    }
    gull.y = Math.max(m.exit.r, gull.y - v);
    if (gull.y <= m.exit.r) {
      gull.x = m.exit.c;
      gull.y = m.exit.r;
      gull.mode = 'active';
      gull.dir = LEFT;
    }
    return;
  }
  if (gull.turnBack) {
    gull.turnBack = false;
    if (gull.mode === 'active') gull.dir = reverseOf(gull.dir);
  }
  let speed = gull.mode === 'eaten' ? EATEN_SPEED : gull.scared ? SCARED_SPEED : gullSpeed(g.level);
  if (gull.mode === 'active' && isTunnel(m, gull.x, gull.y)) speed *= 0.55;
  const decide = (c: number, r: number): number => {
    if (gull.mode === 'eaten') {
      if (c === m.exit.c && r === m.exit.r) {
        gull.mode = 'entering';
        return NONE;
      }
      // Gli occhi tornano al nido per la strada più corta (anche facendo inversione).
      let bestDir = reverseOf(gull.dir);
      let bestDist = Infinity;
      for (const d of [UP, LEFT, DOWN, RIGHT]) {
        if (!walkable(m, c + DX[d], r + DY[d])) continue;
        const dist = m.homeDist[(r + DY[d]) * m.cols + wrapCol(m, c + DX[d])];
        if (dist >= 0 && dist < bestDist) {
          bestDist = dist;
          bestDir = d;
        }
      }
      return bestDir;
    }
    const back = reverseOf(gull.dir);
    const options = [UP, LEFT, DOWN, RIGHT].filter((d) => d !== back && walkable(m, c + DX[d], r + DY[d]));
    if (options.length === 0) return back;
    if (gull.scared && gull.mode === 'active') return options[Math.min(options.length - 1, Math.floor(rnd() * options.length))];
    const [tx, ty] = gullTarget(g, gull);
    let best = options[0];
    let bestD = Infinity;
    for (const d of options) {
      const dd = (c + DX[d] - tx) ** 2 + (r + DY[d] - ty) ** 2;
      if (dd < bestD) {
        bestD = dd;
        best = d;
      }
    }
    return best;
  };
  // Fermo al centro di una cella con il muro davanti (non dovrebbe capitare): sceglie di nuovo.
  if (atCenter(gull) && !walkable(m, Math.round(gull.x) + DX[gull.dir], Math.round(gull.y) + DY[gull.dir])) {
    gull.dir = decide(Math.round(gull.x), Math.round(gull.y));
  }
  advance(m, gull, speed * h, decide);
}

function movePolpo(g: Game, h: number): void {
  const m = getMaze(g.mazeKey);
  const p = g.polpo;
  const open = (c: number, r: number, d: number) => d >= 0 && walkable(m, c + DX[d], r + DY[d]);
  if (p.want !== NONE && p.want === reverseOf(p.dir)) p.dir = p.want;
  if (atCenter(p)) {
    const c = Math.round(p.x);
    const r = Math.round(p.y);
    if (open(c, r, p.want)) p.dir = p.want;
    else if (!open(c, r, p.dir)) p.dir = NONE;
  }
  const speed = polpoSpeed(g.level) * (g.frightT > 0 ? 1.08 : 1);
  advance(m, p, speed * h, (c, r) => (open(c, r, p.want) ? p.want : open(c, r, p.dir) ? p.dir : NONE));
}

const wrapDist2 = (m: Maze, a: Actor, b: Actor) => {
  let dx = Math.abs(a.x - b.x);
  dx = Math.min(dx, m.cols - dx);
  return dx * dx + (a.y - b.y) ** 2;
};

function addPoints(g: Game, points: number, ev: GameEvent[]): void {
  g.score += points;
  if (!g.extraLife && g.score >= EXTRA_LIFE_AT) {
    g.extraLife = true;
    g.lives += 1;
    ev.push({ type: 'life' });
  }
}

function tick(g: Game, h: number, rnd: Random, ev: GameEvent[]): void {
  const m = getMaze(g.mazeKey);
  g.playT += h;

  // Il peperoncino finisce.
  if (g.frightT > 0) {
    g.frightT -= h;
    if (g.frightT <= 0) {
      g.frightT = 0;
      for (const gull of g.gulls) gull.scared = false;
    }
  } else {
    // Ritirata o caccia: al cambio i gabbiani fanno inversione.
    g.modeT += h;
    const sched = schedule(g.level);
    let acc = 0;
    let chase = true;
    for (let i = 0; i < sched.length; i += 1) {
      acc += sched[i];
      if (g.modeT < acc) {
        chase = i % 2 === 1;
        break;
      }
    }
    if (chase !== g.chase) {
      g.chase = chase;
      for (const gull of g.gulls) if (gull.mode === 'active') gull.turnBack = true;
    }
  }

  // Escono dal nido.
  for (const gull of g.gulls) if (gull.mode === 'house' && g.playT >= releaseAt(gull.id, g.level)) gull.mode = 'leaving';

  movePolpo(g, h);

  // Mangia quello che c'è nella sua cella.
  const p = g.polpo;
  const i = Math.round(p.y) * m.cols + wrapCol(m, Math.round(p.x));
  const dot = g.dots[i];
  if (dot) {
    g.dots[i] = 0;
    g.dotsLeft -= 1;
    g.dotsEaten += 1;
    g.levelDots += 1;
    if (dot === PEPPER) {
      addPoints(g, POINTS.pepper, ev);
      ev.push({ type: 'pepper' });
      g.frightT = frightSeconds(g.level);
      g.chain = 0;
      for (const gull of g.gulls) {
        if (gull.mode === 'eaten' || gull.mode === 'entering') continue;
        gull.scared = true;
        if (gull.mode === 'active') gull.turnBack = true;
      }
    } else {
      addPoints(g, POINTS.tarallo, ev);
      ev.push({ type: 'dot' });
    }
    if (g.bonusCount < BONUS_AT.length && g.levelDots >= BONUS_AT[g.bonusCount]) {
      g.bonusCount += 1;
      g.bonusT = BONUS_S;
    }
  }

  // Il panzerotto.
  if (g.bonusT > 0) {
    g.bonusT = Math.max(0, g.bonusT - h);
    if (wrapDist2(m, p, { x: m.start.c, y: m.start.r, dir: NONE }) < 0.36) {
      g.bonusT = 0;
      addPoints(g, POINTS.bonus, ev);
      ev.push({ type: 'bonus', points: POINTS.bonus, x: m.start.c, y: m.start.r });
    }
  }

  for (const gull of g.gulls) moveGull(g, gull, h, rnd);

  // Incontri.
  for (const gull of g.gulls) {
    if (gull.mode !== 'active' || wrapDist2(m, p, gull) >= 0.36) continue;
    if (gull.scared) {
      const points = POINTS.gulls[Math.min(g.chain, POINTS.gulls.length - 1)];
      g.chain += 1;
      gull.scared = false;
      gull.mode = 'eaten';
      addPoints(g, points, ev);
      ev.push({ type: 'gull', points, x: gull.x, y: gull.y });
    } else {
      g.phase = 'dying';
      g.phaseT = 0;
      ev.push({ type: 'death' });
      return;
    }
  }

  if (g.dotsLeft <= 0) {
    g.phase = 'cleared';
    g.phaseT = 0;
    ev.push({ type: 'cleared' });
  }
}

/** Fa passare `dt` secondi (al massimo 0,1 per volta). Torna quello che è successo. */
export function step(g: Game, dt: number, rnd: Random = Math.random): GameEvent[] {
  const ev: GameEvent[] = [];
  const d = Math.max(0, Math.min(dt, 0.1));
  g.t += d;
  if (g.phase === 'over') return ev;
  if (g.phase !== 'play') {
    g.phaseT += d;
    if (g.phase === 'ready' && g.phaseT >= READY_S) {
      g.phase = 'play';
      g.phaseT = 0;
    } else if (g.phase === 'dying' && g.phaseT >= DEATH_S) {
      g.lives -= 1;
      if (g.lives <= 0) {
        g.lives = 0;
        g.phase = 'over';
        ev.push({ type: 'over' });
      } else {
        resetActors(g);
        g.phase = 'ready';
        g.phaseT = 0;
      }
    } else if (g.phase === 'cleared' && g.phaseT >= CLEARED_S) {
      startLevel(g, g.level + 1);
    }
    return ev;
  }
  let left = d;
  while (left > 1e-9 && g.phase === 'play') {
    const h = Math.min(SUB, left);
    left -= h;
    tick(g, h, rnd, ev);
  }
  return ev;
}

/** La direzione chiesta dal dito o dalle frecce. */
export function steer(g: Game, dir: number): void {
  if (dir >= 0 && dir < 4) g.polpo.want = dir;
}

// ---- Salvataggio (si riprende dall'inizio del livello, con i taralli già mangiati) -----

const SAVE_KEY = 'nva.polpo.game.v1';
const BEST_KEY = 'nva.polpo.best.v1';

export function saveGame(g: Game): void {
  try {
    if (g.phase === 'over') {
      window.localStorage.removeItem(SAVE_KEY);
      return;
    }
    window.localStorage.setItem(SAVE_KEY, JSON.stringify({
      v: 1,
      level: g.level,
      score: g.score,
      lives: g.lives,
      dotsEaten: g.dotsEaten,
      extraLife: g.extraLife,
      levelDots: g.levelDots,
      bonusCount: g.bonusCount,
      dots: Array.from(g.dots).join(''),
    }));
  } catch {
    // niente
  }
}

export function loadGame(): Game | null {
  try {
    const raw = JSON.parse(window.localStorage.getItem(SAVE_KEY) || 'null');
    if (!raw || raw.v !== 1) return null;
    const level = Number(raw.level);
    if (!Number.isInteger(level) || level < 1 || level > 500) return null;
    const m = getMaze(mazeKeyForLevel(level));
    const dots = String(raw.dots || '');
    if (dots.length !== m.cols * m.rows || !/^[012]+$/.test(dots)) return null;
    const g = newGame();
    startLevel(g, level);
    // Solo dove c'era un tarallo (o un peperoncino) all'inizio del livello, e dello stesso tipo.
    g.dots = Uint8Array.from(dots, (ch, i) => (m.dots[i] && ch !== '0' ? m.dots[i] : 0));
    g.dotsLeft = g.dots.reduce((n, v) => n + (v ? 1 : 0), 0);
    if (g.dotsLeft === 0) return null;
    g.score = Math.max(0, Math.floor(Number(raw.score) || 0));
    g.lives = Math.min(9, Math.max(1, Math.floor(Number(raw.lives) || 1)));
    g.dotsEaten = Math.max(0, Math.floor(Number(raw.dotsEaten) || 0));
    g.extraLife = !!raw.extraLife;
    g.levelDots = Math.max(0, Math.floor(Number(raw.levelDots) || 0));
    g.bonusCount = Math.min(BONUS_AT.length, Math.max(0, Math.floor(Number(raw.bonusCount) || 0)));
    return g;
  } catch {
    return null;
  }
}

export function clearSavedGame(): void {
  try {
    window.localStorage.removeItem(SAVE_KEY);
  } catch {
    // niente
  }
}

export function loadBest(): number {
  try {
    return Number(window.localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function saveBest(score: number): void {
  try {
    if (score > loadBest()) window.localStorage.setItem(BEST_KEY, String(score));
  } catch {
    // niente
  }
}
